import React, { useRef, useLayoutEffect, useState } from 'react'
import styled from 'styled-components'
import media from '../../utils/style'
import BackgroundShape from '../../vectors/BackgroundShape'

const FeatureHeroEl = styled.div`
  position: relative;
  width: 100%;
  color: ${(props) => props.color || '#000'};
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  overflow: hidden;
  min-height: 360px;

  ${media.MediumScreenUp`
    min-height: 560px;
    height: 100%; /* IE fix */
  `};
`

const FeatureHeroImage = styled.div`
  position: absolute;
  top: 0;
  left: 20%;
  right: 0;
  bottom: 0;
  background-size: cover;
  background-position: 20% 50%;
`

const FeatureHeroContent = styled.div`
  padding: 16px 20px 24px;
  z-index: 1;
  text-align: left;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  font-weight: 400;
  font-size: 80%;
  width: 350px;

  ${media.MediumScreenUp`
    padding: 32px;
    font-size: 100%;
    width: 560px;
  `}
`

const FeatureHeroContentInner = styled.div`
  position: relative;
  float: left;
  height: 100%;
  line-height: 1.5;
  margin-left: auto;
  margin-right: auto;

  > * {
    margin-bottom: 0;
    float: left;
  }

  > * + * {
    margin-top: 16px;
    float: left;
  }
`

const FeatureHeroContentTop = styled.div`
  :before {
    content: '';
    float: right;
    width: 55%;
    height: 200px;

    shape-outside: polygon(98.39% 5px, 100% 100%, 29.44% 87.5%);
    shape-margin: 10px;

    clip-path: polygon(98.39% 5px, 100% 100%, 29.44% 87.5%);
    z-index: -1;
  }
`

const FeatureHeroContentBottom = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;

  :before {
    content: '';
    float: right;
    width: 75%;
    height: ${(props) => props.height || '300px'};

    shape-outside: polygon(93.96% 21px, 97.13% 96.85%, 34.28% 12.4%);
    shape-margin: 20px;
    clip-path: polygon(93.96% 21px, 97.13% 96.85%, 34.28% 12.4%);
    z-index: -1;
  }
`

const FeatureHeroHeading = styled.h2`
  font-size: 30px;
  color: ${(props) => props.color || '#000'};
  flex: 1 1 100%;
  display: block;
  ${media.MediumScreenUp`
    font-size: 48px;
  `};
`

const FeatureHeroDescription = styled.div`
  display: block;
  height: auto;
  padding-top: 16px;

  * {
    font-size: 90%;
    margin-bottom: 0;
    color: ${(props) => props.color || '#000'};

    ${media.MediumScreenUp`
      font-size: 100%;
    `};
  }
`

const FeatureHeroTerms = styled.div`
  font-size: 11px;
  line-height: 1.1;
  font-style: italic;
  opacity: 0.8;
  padding-top: 16px;

  > * {
    margin: 0;
  }
`

const FeatureHeroOverlay = styled.a`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  opacity: 0.5;
`

const FeatureHeroAction = styled.div`
  position: relative;
  z-index: 1;
  pointer-events: none;
  padding-top: 16px;

  * {
    pointer-events: all;
  }
`

const FeatureHeroButton = styled.a`
  background: none;
  border: 0;
  font-weight: bold;
  color: ${(props) => props.color || '#000'};
  text-decoration: underline;
  transition: color 0.3s ease-in-out;

  &:hover,
  &:focus,
  &:active {
    transition-duration: 0.1s;
    color: ${(props) => props.color || '#000'};
  }
`

const Background = styled(BackgroundShape)`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  height: 100%;
  width: auto;

  g {
    fill: ${(props) => props.color || '#fff'};
  }
`

const FeatureHero = (props) => {
  const {
    title,
    description,
    terms,
    callToActionText,
    callToActionLink,
    onMouseDown,
    onClick,
    backgroundColor,
    textColor,
    src
  } = props

  const bottomHero = useRef(null)
  const [height, setHeight] = useState('auto')

  // make sure the bottom hero :before is always the height of the bottom hero
  useLayoutEffect(() => {
    if (bottomHero.current) {
      setHeight(bottomHero.current.scrollHeight + 'px')
    }
  }, [bottomHero.current])

  return (
    <FeatureHeroEl color={textColor}>
      <FeatureHeroImage style={{ backgroundImage: `url(${src})` }} />
      <Background color={backgroundColor} />
      <FeatureHeroContent>
        <FeatureHeroContentInner>
          <FeatureHeroContentTop>
            <FeatureHeroHeading color={textColor}>{title}</FeatureHeroHeading>
          </FeatureHeroContentTop>
          <FeatureHeroContentBottom height={height}>
            <div ref={bottomHero}>
              {description && (
                <FeatureHeroDescription
                  color={textColor}
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              )}
              {terms && (
                <FeatureHeroTerms color={textColor}>
                  {terms.split(/\n/).map((line, i) => (
                    <div key={`line:${i}`}>{line}</div>
                  ))}
                </FeatureHeroTerms>
              )}
              {callToActionText && (
                <FeatureHeroAction color={textColor}>
                  <FeatureHeroButton
                    color={textColor}
                    onMouseDown={onMouseDown}
                    onClick={onClick}
                    href={callToActionLink}
                  >
                    {callToActionText}
                  </FeatureHeroButton>
                </FeatureHeroAction>
              )}
            </div>
          </FeatureHeroContentBottom>
        </FeatureHeroContentInner>
      </FeatureHeroContent>
      <FeatureHeroOverlay
        href={callToActionLink}
        onMouseDown={onMouseDown}
        onClick={onClick}
      />
    </FeatureHeroEl>
  )
}

export default FeatureHero
